import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  Shield,
  ShieldCheck,
  Key,
  Mail,
  User,
  Loader2,
  Eye,
  EyeOff,
  X,
  AlertTriangle,
  CheckCircle,
  Crown,
  Lock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const createAuthAxios = () => {
  const instance = axios.create();
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  return instance;
};

const authAxios = createAuthAxios();

export const AdminUsersManager = ({ currentAdmin }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  
  // Form states
  const [createForm, setCreateForm] = useState({ email: '', password: '', name: '' });
  const [editForm, setEditForm] = useState({ name: '', email: '', is_active: true });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await authAxios.get(`${API}/admin/users`);
      setUsers(res.data);
    } catch (error) {
      console.error('Error fetching admin users:', error);
      toast.error('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.password || !createForm.name) {
      toast.error('All fields are required');
      return;
    }
    
    setSubmitting(true);
    try {
      await authAxios.post(`${API}/admin/users`, createForm);
      toast.success('Admin user created successfully');
      setShowCreateModal(false);
      setCreateForm({ email: '', password: '', name: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!showEditModal) return;
    
    setSubmitting(true);
    try {
      await authAxios.put(`${API}/admin/users/${showEditModal.id}`, editForm);
      toast.success('Admin user updated successfully');
      setShowEditModal(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;
    
    setSubmitting(true);
    try {
      await authAxios.delete(`${API}/admin/users/${showDeleteConfirm.id}`);
      toast.success('Admin user deleted successfully');
      setShowDeleteConfirm(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!showResetPassword) return;
    
    setSubmitting(true);
    try {
      const res = await authAxios.post(`${API}/admin/users/reset-password/${showResetPassword.id}`);
      setTempPassword(res.data.temporary_password);
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (user) => {
    setEditForm({ name: user.name, email: user.email, is_active: user.is_active });
    setShowEditModal(user);
  };

  const isMasterAdmin = currentAdmin?.email?.toLowerCase() === 'lamar@medexova.com';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-3 rounded-xl bg-neon-purple/20">
            <Users className="w-6 h-6 text-neon-purple" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Admin Users</h2>
            <p className="text-sm text-slate-400">Manage admin accounts and permissions</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-neon-purple rounded-lg text-white hover:bg-neon-purple/80 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add Admin</span>
        </button>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-neon-cyan animate-spin" />
        </div>
      ) : (
        <div className="grid gap-4">
          {users.map((user) => {
            const canEdit = !user.is_master || isMasterAdmin;
            const canDelete = !user.is_master && user.email !== currentAdmin?.email;
            
            return (
              <motion.div
                key={user.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-xl border ${
                  user.is_master 
                    ? 'bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-amber-500/30' 
                    : 'bg-white/5 border-white/10'
                } ${!user.is_active ? 'opacity-60' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Avatar */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      user.is_master ? 'bg-amber-500/20' : 'bg-neon-purple/20'
                    }`}>
                      {user.is_master ? (
                        <Crown className="w-6 h-6 text-amber-400" />
                      ) : (
                        <User className="w-6 h-6 text-neon-purple" />
                      )}
                    </div>
                    
                    {/* Info */}
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-white">{user.name}</span>
                        {user.is_master && (
                          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full flex items-center space-x-1">
                            <ShieldCheck className="w-3 h-3" />
                            <span>MASTER</span>
                          </span>
                        )}
                        {user.must_change_password && (
                          <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full flex items-center space-x-1">
                            <Key className="w-3 h-3" />
                            <span>Password Change Required</span>
                          </span>
                        )}
                        {!user.is_active && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-slate-400">
                        <Mail className="w-3 h-3" />
                        <span>{user.email}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        Last login: {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    {canEdit && (
                      <button
                        onClick={() => openEditModal(user)}
                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                    {canEdit && (
                      <button
                        onClick={() => { setShowResetPassword(user); setTempPassword(null); }}
                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                        title="Reset Password"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => setShowDeleteConfirm(user)}
                        className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {user.is_master && !isMasterAdmin && (
                      <div className="p-2 text-slate-600" title="Protected Account">
                        <Lock className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-800 rounded-2xl p-6 w-full max-w-md mx-4 border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Create Admin User</h3>
                <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="text"
                      value={createForm.name}
                      onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-neon-purple/50 focus:outline-none"
                      placeholder="John Smith"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="email"
                      value={createForm.email}
                      onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-neon-purple/50 focus:outline-none"
                      placeholder="admin@medexova.com"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Password</label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={createForm.password}
                      onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                      className="w-full pl-10 pr-12 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-neon-purple/50 focus:outline-none"
                      placeholder="Min 6 characters"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">User will be prompted to change password on first login</p>
                </div>
                
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-neon-purple rounded-lg font-semibold text-white hover:bg-neon-purple/80 disabled:opacity-50 flex items-center justify-center"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Admin'}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setShowEditModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-800 rounded-2xl p-6 w-full max-w-md mx-4 border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Edit Admin User</h3>
                <button onClick={() => setShowEditModal(null)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleEdit} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-neon-purple/50 focus:outline-none"
                      required
                    />
                  </div>
                </div>
                
                {!showEditModal.is_master && (
                  <div>
                    <label className="block text-sm text-slate-300 mb-1">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-neon-purple/50 focus:outline-none"
                        required
                      />
                    </div>
                  </div>
                )}
                
                {!showEditModal.is_master && (
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                      className="w-4 h-4 rounded bg-white/10 border-white/20 text-neon-purple focus:ring-neon-purple"
                    />
                    <label htmlFor="is_active" className="text-slate-300">Account Active</label>
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-neon-purple rounded-lg font-semibold text-white hover:bg-neon-purple/80 disabled:opacity-50 flex items-center justify-center"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save Changes'}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-800 rounded-2xl p-6 w-full max-w-md mx-4 border border-red-500/30"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-3 rounded-full bg-red-500/20">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-white">Delete Admin User</h3>
              </div>
              
              <p className="text-slate-300 mb-6">
                Are you sure you want to delete <span className="font-semibold text-white">{showDeleteConfirm.name}</span> ({showDeleteConfirm.email})? 
                This action cannot be undone.
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 py-3 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={submitting}
                  className="flex-1 py-3 bg-red-500 rounded-lg font-semibold text-white hover:bg-red-600 disabled:opacity-50 flex items-center justify-center"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Delete'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reset Password Modal */}
      <AnimatePresence>
        {showResetPassword && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => { setShowResetPassword(null); setTempPassword(null); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-800 rounded-2xl p-6 w-full max-w-md mx-4 border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-3 rounded-full bg-amber-500/20">
                  <Key className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="text-xl font-bold text-white">Reset Password</h3>
              </div>
              
              {!tempPassword ? (
                <>
                  <p className="text-slate-300 mb-6">
                    Reset password for <span className="font-semibold text-white">{showResetPassword.name}</span>? 
                    A temporary password will be generated.
                  </p>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => { setShowResetPassword(null); setTempPassword(null); }}
                      className="flex-1 py-3 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleResetPassword}
                      disabled={submitting}
                      className="flex-1 py-3 bg-amber-500 rounded-lg font-semibold text-white hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-6">
                    <div className="flex items-center space-x-2 text-green-400 mb-3">
                      <CheckCircle className="w-5 h-5" />
                      <span>Password reset successfully!</span>
                    </div>
                    <p className="text-slate-400 text-sm mb-3">
                      Share this temporary password with {showResetPassword.name}. They will be required to change it on next login.
                    </p>
                    <div className="p-4 bg-slate-700 rounded-lg">
                      <p className="text-xs text-slate-400 mb-1">Temporary Password:</p>
                      <p className="text-xl font-mono text-white tracking-wider">{tempPassword}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => { setShowResetPassword(null); setTempPassword(null); }}
                    className="w-full py-3 bg-neon-purple rounded-lg font-semibold text-white hover:bg-neon-purple/80"
                  >
                    Done
                  </button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminUsersManager;
